<script setup lang="ts">
import { Components } from '@/api/generated/api';
import { UserRoleType } from '@/api/generated/api.enum';
import BillingProBadge from '@/core/billing/BillingProBadge.vue';
import CustomCheckbox from '@/core/elements/CustomCheckbox.vue';
import SchoolSubjectSearchSelect from '@/core/school-subjects/SchoolSubjectSearchSelect.vue';
import RadioGroup, { IRadioGroupOption } from '@/core/selects/RadioGroup.vue';
import { IUserWithPermissions } from '@/interfaces';
import { useContainerTranslations } from '@/schoolClasses/useContainerTranslations';
import { assertNotNullOrUndefined } from '@/utils/assertion.utils';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import InfoTooltip from '../elements/InfoTooltip.vue';

const { t } = useI18n();

const emit = defineEmits<{
  'update:user': [value: IUserWithPermissions];
  'update:isValid': [value: boolean];
}>();

// The accessRights feature lock is supplied by the parent so this form stays tenant-scoping agnostic
// (reused in provider context, where billing is not the acting user's own tenant).
const props = withDefaults(
  defineProps<{
    user: IUserWithPermissions;
    availableSchoolSubjects: Components.Schemas.SchoolSubjectDto[];
    availableSchoolSubjectGroups: Components.Schemas.SchoolSubjectGroupDto[];
    disabled?: boolean;
    mode?: 'archive' | 'edit';
    isNewAccessRight?: boolean;
    isAccessRightsLocked?: boolean;
    isBillingStatusLoading?: boolean;
  }>(),
  {
    mode: 'edit',
    isAccessRightsLocked: false,
    isBillingStatusLoading: false,
  },
);
const { user, availableSchoolSubjects, availableSchoolSubjectGroups, disabled } = toRefs(props);

// Managing access rights is a PRO feature. Subject access stays configurable without it; only the
// management permissions are locked.
const isAccessRightsLocked = computed<boolean>(() => props.isAccessRightsLocked);
const isAdminUser = computed<boolean>(() => user.value.userRoleType === UserRoleType.admin);
// Existing admins are displayed with effective full access, but the stored access-right rows are
// intentionally left unchanged so their previous constraints apply again after a later downgrade.
const showExistingAdminFullAccess = computed<boolean>(() => isAdminUser.value && !props.isNewAccessRight);
const isSubjectSelectionDisabled = computed<boolean>(() => !!disabled.value || showExistingAdminFullAccess.value);
const isManagementPermissionsDisabled = computed<boolean>(
  () => !!disabled.value || isAccessRightsLocked.value || showExistingAdminFullAccess.value,
);

// a group owns its single subject, so members always get full subject access — the picker is hidden
// and the explanations name the group instead of the class
const { isGroup, tContainer } = useContainerTranslations(() => user.value.schoolClass);

const isInitialized = ref<boolean>(false);
const userInternal = ref<IUserWithPermissions | null>(null);
const hasAccessToAllSubjects = ref<IRadioGroupOption>({
  label: t('general.all'),
  value: true,
});
const hasAccessToSpecificSubjects = ref<IRadioGroupOption>({
  label: t('accessRights.selectSpecific'),
  value: false,
});
const subjectAccessOptions = ref<IRadioGroupOption[]>([
  hasAccessToAllSubjects.value,
  hasAccessToSpecificSubjects.value,
]);
const selectedSchoolSubjects = ref<Components.Schemas.SchoolSubjectDto[]>([]);

const selectedSchoolSubjectIds = computed<number[]>(() => selectedSchoolSubjects.value.map((s) => s.id));
const canManageStudentsModel = computed<boolean>(
  () => showExistingAdminFullAccess.value || !!userInternal.value?.permissions.canManageStudents,
);
const canManageCompetencesModel = computed<boolean>(
  () => showExistingAdminFullAccess.value || !!userInternal.value?.permissions.canManageCompetences,
);
const canManagePerformanceAssessmentsModel = computed<boolean>(
  () => showExistingAdminFullAccess.value || !!userInternal.value?.permissions.canManagePerformanceAssessments,
);
const canManageAccessRightsModel = computed<boolean>(
  () => showExistingAdminFullAccess.value || !!userInternal.value?.permissions.canManageAccessRights,
);

const baseSelectedSchoolSubject = computed<Components.Schemas.BaseSchoolSubjectDto[]>(() =>
  selectedSchoolSubjects.value.map((s) => ({
    id: s.id,
    originId: s.originId,
    name: s.name,
    abbreviation: s.abbreviation,
    color: s.color,
  })),
);

// The subject list only holds the subjects the editor can access, so a restriction may also contain subjects
// they cannot see. Those stay part of the restriction and keep it valid instead of reading as an empty selection.
const hiddenSchoolSubjects = computed<Components.Schemas.BaseSchoolSubjectDto[]>(() => {
  const availableSubjectIds = new Set(availableSchoolSubjects.value.map((s) => s.id));
  return (userInternal.value?.permissions.subjects ?? []).filter((s) => !availableSubjectIds.has(s.id));
});
const hasSelectedSubjects = computed<boolean>(
  () => selectedSchoolSubjectIds.value.length > 0 || hiddenSchoolSubjects.value.length > 0,
);

const emitPermissionChanges = (permission: keyof IUserWithPermissions['permissions'], value: boolean): void => {
  assertNotNullOrUndefined(userInternal.value);
  userInternal.value = {
    ...userInternal.value,
    permissions: {
      ...userInternal.value.permissions,
      [permission]: value,
    },
  };
  emit('update:user', userInternal.value);
};

const emitSchoolSubjectChanges = (
  subjects: Components.Schemas.SchoolSubjectDto[],
  { keepHiddenSubjects }: { keepHiddenSubjects: boolean },
): void => {
  assertNotNullOrUndefined(userInternal.value);
  const hiddenSubjects = keepHiddenSubjects ? hiddenSchoolSubjects.value : [];
  userInternal.value = {
    ...userInternal.value,
    permissions: {
      ...userInternal.value.permissions,
      subjects: [...baseSelectedSchoolSubject.value, ...hiddenSubjects],
    },
    schoolSubjects: subjects,
  };

  emit('update:user', userInternal.value);
};

const handleSubjectSelection = (options: IRadioGroupOption[]): void => {
  // Check which ratio is selected and set access correctly
  const optionAllSubjects = options.find((o) => o.label === hasAccessToAllSubjects.value.label);
  const optionSpecificSubjects = options.find((o) => o.label === hasAccessToSpecificSubjects.value.label);
  if (optionAllSubjects) hasAccessToAllSubjects.value.value = !!optionAllSubjects.value;
  if (optionSpecificSubjects) hasAccessToSpecificSubjects.value.value = !!optionSpecificSubjects.value;

  subjectAccessOptions.value = options;
  selectedSchoolSubjects.value = [];
  // "all" includes the subjects the editor cannot see, so only a specific selection keeps them
  emitSchoolSubjectChanges([], { keepHiddenSubjects: hasAccessToSpecificSubjects.value.value });
};

const setSubjectAccessMode = (hasSpecificSubjects: boolean): void => {
  hasAccessToAllSubjects.value.value = !hasSpecificSubjects;
  hasAccessToSpecificSubjects.value.value = hasSpecificSubjects;
  subjectAccessOptions.value = [
    { ...hasAccessToAllSubjects.value, value: !hasSpecificSubjects },
    { ...hasAccessToSpecificSubjects.value, value: hasSpecificSubjects },
  ];
};

const handleSchoolSubjectsSelected = (value: number[]): void => {
  // Set radio and accesses correctly
  setSubjectAccessMode(true);
  selectedSchoolSubjects.value = availableSchoolSubjects.value.filter((s) => value.includes(s.id));
  emitSchoolSubjectChanges(selectedSchoolSubjects.value, { keepHiddenSubjects: true });
};

const showAllSubjects = (): void => {
  setSubjectAccessMode(false);
  selectedSchoolSubjects.value = [];
};

const syncDisplayedSubjectAccess = (): void => {
  if (isGroup.value) {
    showAllSubjects();
    return;
  }

  selectedSchoolSubjects.value = availableSchoolSubjects.value.filter((s) =>
    user.value.permissions.subjects?.map((ms) => ms.id).includes(s.id),
  );

  if (showExistingAdminFullAccess.value) {
    showAllSubjects();
    return;
  }

  setSubjectAccessMode(!!user.value.permissions.subjects?.length);
};

// Adjusted management permissions applied to new access rights when the feature is locked.
const applyLockedManagementPermissions = (): void => {
  assertNotNullOrUndefined(userInternal.value);
  const canManage = isAdminUser.value;
  userInternal.value = {
    ...userInternal.value,
    permissions: {
      ...userInternal.value.permissions,
      canManageAccessRights: canManage,
      canManageStudents: canManage,
      canManageCompetences: canManage,
      canManagePerformanceAssessments: canManage,
    },
  };
  emit('update:user', userInternal.value);
};

// Retruns error if radio is set to specific but no subjects are selected
const hasSpecificSubjectError = computed(() => {
  const hasOptionSpecificSet =
    subjectAccessOptions.value.find((option) => option.label === hasAccessToSpecificSubjects.value.label)?.value ??
    false;

  const hasOptionAll =
    subjectAccessOptions.value.find((option) => option.label === hasAccessToAllSubjects.value.label)?.value ?? false;

  return hasOptionSpecificSet && !hasOptionAll && !hasSelectedSubjects.value;
});

const isSUbjectSelectionValid = computed(() => {
  if (isGroup.value) return true;
  if (hasAccessToAllSubjects.value.value) return true;
  if (hasAccessToSpecificSubjects.value.value) return hasSelectedSubjects.value;
  return false;
});

watch(
  isSUbjectSelectionValid,
  (val) => {
    emit('update:isValid', val);
  },
  { immediate: true },
);

watch(
  user,
  () => {
    userInternal.value = user.value;
  },
  { immediate: true },
);

// Normalize a NEW access right's management permissions once — but only after the billing/feature state
// has finished loading, so we never act on the fail-open defaults (`hasFeature` returns true while
// loading). Existing access rights are never touched (no phantom "unsaved changes").
watch(
  () => props.isBillingStatusLoading,
  (loading) => {
    if (loading || isInitialized.value) return;

    if (isAccessRightsLocked.value && props.isNewAccessRight) {
      applyLockedManagementPermissions();
    }

    isInitialized.value = true;
  },
  { immediate: true },
);

watch(
  [
    () => user.value.user.id,
    () => user.value.schoolClass?.id,
    () => user.value.userRoleType,
    () => props.isNewAccessRight,
    availableSchoolSubjects,
  ],
  () => {
    syncDisplayedSubjectAccess();
  },
  { immediate: true },
);
</script>

<template>
  <div v-if="userInternal" class="flex flex-col gap-sm">
    <CustomCheckbox :model-value="true" :disabled="true">
      {{ mode === 'edit' ? $t('accessRights.canCreateCompetences') : $t('accessRights.hasArchiveReadRights') }}
    </CustomCheckbox>
    <div v-if="!isGroup" class="md:pl-[27px]">
      <RadioGroup
        :options="subjectAccessOptions"
        :disabled="isSubjectSelectionDisabled"
        @update:options="handleSubjectSelection"
      >
        <template #option="{ option }">
          <SchoolSubjectSearchSelect
            v-if="option?.label === hasAccessToSpecificSubjects.label"
            class="w-full"
            :error="hasSpecificSubjectError"
            :all-school-subjects="availableSchoolSubjects"
            :available-school-subject-groups="availableSchoolSubjectGroups"
            :disabled="isSubjectSelectionDisabled"
            :model-value="selectedSchoolSubjectIds"
            can-select-multiple
            theme="input"
            @update:model-value="handleSchoolSubjectsSelected"
          />
        </template>
      </RadioGroup>
    </div>

    <template v-if="mode === 'edit'">
      <div v-if="isAccessRightsLocked" class="flex items-center gap-sm mb-sm">
        <BillingProBadge theme="gray" />
        <InfoTooltip :tooltip-text="tContainer('proAccessRightsTooltip')" content-style="w-[280px]" />
      </div>
      <CustomCheckbox
        :model-value="canManageStudentsModel"
        :disabled="isManagementPermissionsDisabled"
        :align-items="'center'"
        @update:model-value="(value) => emitPermissionChanges('canManageStudents', value)"
      >
        <span>
          {{ $t('accessRights.canManageStudents') }}
          <span class="inline-block align-top mt-[2px]">
            <InfoTooltip :tooltip-text="tContainer('canManageStudentsTooltip')" content-style="w-[280px]" />
          </span>
        </span>
      </CustomCheckbox>

      <CustomCheckbox
        :model-value="canManageCompetencesModel"
        :disabled="isManagementPermissionsDisabled"
        :align-items="'center'"
        @update:model-value="(value) => emitPermissionChanges('canManageCompetences', value)"
      >
        <span>
          {{ $t('accessRights.canManageCompetences') }}
          <span class="inline-block align-top mt-[2px]">
            <InfoTooltip :tooltip-text="t('accessRights.canManageCompetencesTooltip')" content-style="w-[280px]" />
          </span>
        </span>
      </CustomCheckbox>

      <CustomCheckbox
        :model-value="canManagePerformanceAssessmentsModel"
        :disabled="isManagementPermissionsDisabled"
        :align-items="'center'"
        @update:model-value="(value) => emitPermissionChanges('canManagePerformanceAssessments', value)"
      >
        <span>
          {{ $t('accessRights.canManagePerformanceAssessments') }}
          <span class="inline-block align-top mt-[2px]">
            <InfoTooltip
              :tooltip-text="t('accessRights.canManagePerformanceAssessmentsTooltip')"
              content-style="w-[280px]"
            />
          </span>
        </span>
      </CustomCheckbox>
    </template>
    <CustomCheckbox
      :class="[mode === 'archive' && 'order-[-1]']"
      :model-value="canManageAccessRightsModel"
      :disabled="isManagementPermissionsDisabled"
      :align-items="'center'"
      @update:model-value="(value) => emitPermissionChanges('canManageAccessRights', value)"
    >
      <span>
        {{ mode === 'edit' ? $t('accessRights.canManageAccessRights') : $t('accessRights.canManageArchiveRights') }}
        <span class="inline-block align-top mt-[2px]">
          <InfoTooltip :tooltip-text="tContainer('canManageAccessRightsTooltip')" content-style="w-[280px]" />
        </span>
      </span>
    </CustomCheckbox>
  </div>
</template>
